/**
 * Single Source of Truth for decision tracks.
 * Used by frontend components. Edge functions have a mirror copy at
 * supabase/functions/_shared/decisionTracks.ts — keep them in sync!
 */

export interface DecisionTrack {
  id: string;
  label: string;
  description: string;
  dbType: string; // maps to questions.decision_type in DB
  scope: string;  // decision_scope value for sessions
  focusAreas: string[]; // concrete Phase 1 focus areas shown on classification confirm
}

export interface DecisionSubtrack {
  id: string;
  label: string;
  description: string;
  parentTrackId: string;
}

export const DECISION_TRACKS: DecisionTrack[] = [
  { id: "strategy", label: "Strategie & Positionierung", description: "Vision, Positionierung, Geschäftsmodell", dbType: "strategy", scope: "org", focusAreas: ["Marktpositionierung", "Wettbewerbsanalyse", "Ressourcenallokation", "Strategische Optionen"] },
  { id: "product", label: "Produkt & Innovation", description: "Features, Roadmap, Produktstrategie", dbType: "product", scope: "product", focusAreas: ["Feature-Bewertung", "Marktfit-Analyse", "Roadmap-Priorisierung", "Technische Machbarkeit"] },
  { id: "growth", label: "Go-to-Market & Wachstum", description: "Vertrieb, Marketing, Expansion", dbType: "growth", scope: "org", focusAreas: ["Kundenakquise", "Channel-Analyse", "Marktpotenzial", "Skalierungsstrategie"] },
  { id: "org_roles", label: "Organisation & Rollen", description: "Struktur, Verantwortlichkeiten, Prozesse", dbType: "talent", scope: "org", focusAreas: ["Organisationsdesign", "Rollenverteilung", "Kompetenzanalyse", "Prozesseffizienz"] },
  { id: "leadership", label: "Menschen & Führung", description: "Team, Kultur, Führungsentscheidungen", dbType: "leadership", scope: "team", focusAreas: ["Teamdynamik", "Führungsansatz", "Konfliktanalyse", "Motivationsstruktur"] },
  { id: "operations", label: "Operations & Prozesse", description: "Abläufe, Effizienz, Skalierung", dbType: "operations", scope: "org", focusAreas: ["Prozessoptimierung", "Toolbewertung", "Kapazitätsplanung", "Qualitätsstandards"] },
  { id: "finance", label: "Finanzen & Wirtschaftlichkeit", description: "Budget, Investment, Rentabilität", dbType: "finance", scope: "org", focusAreas: ["ROI-Analyse", "Kostenstruktur", "Cashflow-Projektion", "Investitionsbewertung"] },
];

export const DECISION_SUBTRACKS: Record<string, DecisionSubtrack[]> = {
  strategy: [
    { id: "focus", label: "Fokussierung", description: "Worauf konzentrieren?", parentTrackId: "strategy" },
    { id: "positioning", label: "Positionierung", description: "Wie differenzieren?", parentTrackId: "strategy" },
    { id: "growth_direction", label: "Wachstumsrichtung", description: "Wohin expandieren?", parentTrackId: "strategy" },
    { id: "pivot", label: "Pivotierung", description: "Richtungswechsel nötig?", parentTrackId: "strategy" },
  ],
  product: [
    { id: "new_product", label: "Neues Produkt", description: "Launch-Entscheidung", parentTrackId: "product" },
    { id: "feature_priority", label: "Feature-Priorisierung", description: "Was bauen wir zuerst?", parentTrackId: "product" },
    { id: "product_strategy", label: "Produktstrategie", description: "Roadmap & Vision", parentTrackId: "product" },
    { id: "pricing", label: "Pricing", description: "Preisgestaltung", parentTrackId: "product" },
  ],
  growth: [
    { id: "acquisition", label: "Kundenakquise", description: "Neue Kunden gewinnen", parentTrackId: "growth" },
    { id: "retention", label: "Retention", description: "Kunden halten", parentTrackId: "growth" },
    { id: "channel_strategy", label: "Channel-Strategie", description: "Welche Kanäle nutzen?", parentTrackId: "growth" },
    { id: "internationalization", label: "Internationalisierung", description: "Neue Märkte erschließen", parentTrackId: "growth" },
  ],
  org_roles: [
    { id: "team_structure", label: "Teamstruktur", description: "Wie organisieren?", parentTrackId: "org_roles" },
    { id: "hiring", label: "Neue Rolle besetzen", description: "Hiring-Entscheidung", parentTrackId: "org_roles" },
    { id: "responsibilities", label: "Verantwortlichkeiten", description: "Wer macht was?", parentTrackId: "org_roles" },
    { id: "other_org", label: "Etwas anderes", description: "Andere org. Frage", parentTrackId: "org_roles" },
  ],
  leadership: [
    { id: "leadership_style", label: "Führungsstil", description: "Wie führe ich?", parentTrackId: "leadership" },
    { id: "conflict_resolution", label: "Konfliktlösung", description: "Team-Konflikte", parentTrackId: "leadership" },
    { id: "team_motivation", label: "Team-Motivation", description: "Team aktivieren", parentTrackId: "leadership" },
    { id: "delegation", label: "Delegation", description: "Was abgeben?", parentTrackId: "leadership" },
  ],
  operations: [
    { id: "process_optimization", label: "Prozessoptimierung", description: "Abläufe verbessern", parentTrackId: "operations" },
    { id: "system_selection", label: "Systemauswahl", description: "Tools & Infrastruktur", parentTrackId: "operations" },
    { id: "quality", label: "Qualitätsmanagement", description: "Standards & Kontrolle", parentTrackId: "operations" },
    { id: "capacity", label: "Kapazitätsplanung", description: "Ressourcen steuern", parentTrackId: "operations" },
  ],
  finance: [
    { id: "investment", label: "Investitionsentscheidung", description: "Wo investieren?", parentTrackId: "finance" },
    { id: "cost_optimization", label: "Kostenoptimierung", description: "Ausgaben senken", parentTrackId: "finance" },
    { id: "pricing_margin", label: "Pricing & Marge", description: "Preisgestaltung", parentTrackId: "finance" },
    { id: "cashflow", label: "Cashflow", description: "Liquidität steuern", parentTrackId: "finance" },
  ],
};

// ── Derived constants ──────────────────────────────────────────

/** Valid decision_type values for DB constraints */
export const VALID_DECISION_TYPES = DECISION_TRACKS.map(t => t.id);

/** All valid subtrack IDs across all tracks */
export const VALID_SUBTRACK_IDS = Object.values(DECISION_SUBTRACKS).flat().map(s => s.id);

/** Map track ID → DB question type */
export const TRACK_TO_DB_TYPE: Record<string, string> = Object.fromEntries(
  DECISION_TRACKS.map(t => [t.id, t.dbType])
);

/** Map track ID → decision scope */
export const TRACK_TO_SCOPE: Record<string, string> = Object.fromEntries(
  DECISION_TRACKS.map(t => [t.id, t.scope])
);

/** Get track by ID */
export const getTrackById = (id: string): DecisionTrack | undefined =>
  DECISION_TRACKS.find(t => t.id === id);

/** Get DB question type from track ID */
export const getDbType = (trackId: string): string => {
  const track = getTrackById(trackId);
  return track?.dbType || 'strategy';
};

/** Get scope from track ID */
export const getScope = (trackId: string): string => {
  const track = getTrackById(trackId);
  return track?.scope || 'org';
};

/** Get subtracks for a track */
export const getSubtracks = (trackId: string): DecisionSubtrack[] =>
  DECISION_SUBTRACKS[trackId] || [];

/** Get subtrack by ID */
export const getSubtrackById = (trackId: string, subtractId: string): DecisionSubtrack | undefined =>
  getSubtracks(trackId).find(s => s.id === subtractId);

/** Validate a decision_type value */
export const isValidTrackId = (id: string): boolean =>
  VALID_DECISION_TYPES.includes(id);

/** Validate a decision_subtype for a given track */
export const isValidSubtrackId = (trackId: string, subtypeId: string): boolean =>
  getSubtracks(trackId).some(s => s.id === subtypeId);
